import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useI18n } from '@runtime/core/i18n/i18n'
import { trapDialogFocus } from '@/shared/a11y/trapDialogFocus'
import styles from './PlayFailure.module.scss'

type Props = { panel?: { open: boolean; close: () => void } }
export default function PlayRecovery({ panel }: Props) {
  const { t } = useI18n()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const heading = useRef<HTMLHeadingElement>(null)
  const dialog = useRef<HTMLDialogElement>(null)
  const playing =
    /^\/strategy\/(?:2p|multi)(?:\/|$)|^\/puzzle\/(?:poster|united\/play|united-online)|^\/online\/room\/|^\/arcade(?:\/|$)|^\/challenges(?:\/|$)/.test(
      pathname,
    )
  const destination =
    playing && pathname.startsWith('/strategy')
      ? '/strategy'
      : playing && pathname.startsWith('/puzzle')
        ? '/puzzle'
        : '/home'
  const isPanel = Boolean(panel)
  const panelOpen = panel?.open ?? true
  useEffect(() => {
    if (!panelOpen) return
    const element = dialog.current
    const previous = document.activeElement
    if (isPanel) element?.showModal()
    heading.current?.focus({ preventScroll: true })
    return () => {
      element?.close()
      if (isPanel && previous instanceof HTMLElement && previous.isConnected)
        previous.focus({ preventScroll: true })
    }
  }, [isPanel, panelOpen])
  if (panel && !panel.open) return null
  const body = (
    <section
      className={styles.failure}
      data-testid="play-recovery"
      aria-labelledby="recovery-title"
    >
      <h1 ref={heading} id="recovery-title" tabIndex={-1}>
        {t('runtime.recovery.title')}
      </h1>
      <p>{t(playing || panel ? 'runtime.recovery.gameBody' : 'runtime.recovery.body')}</p>
      <div className={styles.actions}>
        {panel ? (
          <button type="button" onClick={panel.close}>
            {t('common.close')}
          </button>
        ) : (
          <>
            {!playing && (
              <button type="button" onClick={() => window.location.reload()}>
                {t('runtime.recovery.retry')}
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                if (!playing || window.confirm(t('runtime.recovery.leave')))
                  navigate(destination, { replace: true })
              }}
            >
              {t('runtime.recovery.return')}
            </button>
          </>
        )}
      </div>
    </section>
  )
  return panel ? (
    <dialog
      ref={dialog}
      className={styles.dialog}
      aria-labelledby="recovery-title"
      onCancel={event => {
        event.preventDefault()
        panel.close()
      }}
      onKeyDown={event => trapDialogFocus(event, dialog.current)}
    >
      {body}
    </dialog>
  ) : (
    body
  )
}
